/**
 * O lugar da página quando o plano da empresa venceu e o módulo está bloqueado: o porquê, os
 * módulos afectados, «Pagar agora» (as subscrições) e os contactos de ajuda. No casco
 * substitui o miolo da página; a página `modulo_bloqueado` também o usa.
 */
import React from "react";
import { Head, usePage } from "@inertiajs/react";
import { FileText, LifeBuoy, Mail, Phone, ShieldX, Wallet } from "lucide-react";
import { Botao, Cartao, IconeTema, Selo } from "@/Logigate/ui";

interface PlanoBloqueadoOverlayProps {
    moduloNome?: string;
    mensagem: string;
    nivel_bloqueio: string;
    plano_bloqueio?: {
        modulos_afetados: number[];
        factura_codigo?: string | null;
        dias_vencido: number;
    };
}

const modulosNomes: Record<number, string> = {
    1: "Facturação",
    2: "Recursos Humanos",
    3: "Gestão Documental",
    4: "Finanças",
    5: "Condomínio",
    6: "Hospitalar",
    7: "Hotelaria",
    9: "Comunicação",
    11: "Logística",
    12: "POS",
};

/** «Precisa de ajuda?» com o email e o telefone da Honga Yetu (também no bloqueio da escola). */
export function ContactosDeAjuda() {
    const contactos = [
        { rotulo: "Email", valor: "geral@hongayetu.com", href: "mailto:geral@hongayetu.com", icone: <Mail className="size-4" aria-hidden /> },
        { rotulo: "Telefone", valor: "+244 948 242 262", href: "tel:+244948242262", icone: <Phone className="size-4" aria-hidden /> },
    ];
    return (
        <Cartao espaco="nenhum" className="overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 sm:px-6">
                <IconeTema cor="blue">
                    <LifeBuoy />
                </IconeTema>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">Precisas de ajuda?</p>
                    <p className="text-[13px] text-zinc-500 dark:text-zinc-400">Fala connosco, respondemos depressa.</p>
                </div>
            </div>
            <div className="grid gap-px border-t border-zinc-100 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-800 sm:grid-cols-2">
                {contactos.map((c) => (
                    <a
                        key={c.rotulo}
                        href={c.href}
                        className="flex min-h-14 items-center gap-3 bg-white px-5 py-3 transition-colors hover:bg-zinc-50 sm:px-6 dark:bg-zinc-900 dark:hover:bg-zinc-800/70"
                    >
                        <span className="text-zinc-400 dark:text-zinc-500">{c.icone}</span>
                        <span className="min-w-0">
                            <span className="block text-xs text-zinc-500 dark:text-zinc-400">{c.rotulo}</span>
                            <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{c.valor}</span>
                        </span>
                    </a>
                ))}
            </div>
        </Cartao>
    );
}

export default function PlanoBloqueadoOverlay({ moduloNome, mensagem, plano_bloqueio }: PlanoBloqueadoOverlayProps) {
    const { base_path } = usePage().props as any;
    const texto = mensagem ?? "";
    const modulosAfetados = plano_bloqueio?.modulos_afetados?.map((id) => modulosNomes[id]).filter(Boolean) || [];
    const dias = plano_bloqueio?.dias_vencido || 0;

    // O título segue a mensagem do servidor: «expirou» é um plano que acabou, o resto venceu.
    const titulo = texto.includes("expirou") ? "Plano expirado" : `Plano vencido há ${dias} ${dias === 1 ? "dia" : "dias"}`;

    return (
        <>
            <Head title="Plano Vencido - Acesso Bloqueado" />
            <div className="mx-auto w-full max-w-xl space-y-4 py-2 sm:py-8">
                <Cartao espaco="nenhum" className="overflow-hidden">
                    <div className="flex flex-col items-center px-5 pb-6 pt-8 text-center sm:px-8">
                        <span className="flex size-16 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-8 ring-red-50/60 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/5">
                            <ShieldX className="size-8" aria-hidden />
                        </span>
                        <h1 className="mt-5 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50 sm:text-2xl">{titulo}</h1>
                        {moduloNome && (
                            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                                O módulo <span className="font-medium text-zinc-700 dark:text-zinc-200">{moduloNome}</span> está bloqueado
                            </p>
                        )}
                        {texto && <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">{texto}</p>}
                        {plano_bloqueio?.factura_codigo && (
                            <Selo variante="ponto" cor="red" tamanho="md" className="mt-4">
                                Factura {plano_bloqueio.factura_codigo}
                            </Selo>
                        )}
                    </div>

                    {modulosAfetados.length > 0 && (
                        <div className="border-t border-zinc-100 px-5 py-4 dark:border-zinc-800 sm:px-8">
                            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Módulos bloqueados</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {modulosAfetados.map((nome) => (
                                    <Selo key={nome} cor="red" tamanho="md">
                                        {nome}
                                    </Selo>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-2 border-t border-zinc-100 bg-zinc-50/70 px-5 py-4 dark:border-zinc-800 dark:bg-white/[0.02] sm:flex-row-reverse sm:px-8">
                        <Botao cor="red" tamanho="lg" icone={<Wallet className="size-5" />} href={`${base_path}/subscricoes`} className="sm:flex-1">
                            Pagar agora
                        </Botao>
                        <Botao variante="neutro" tamanho="lg" icone={<FileText className="size-5" />} href={`${base_path}/planos`} className="sm:flex-1">
                            Ver detalhes do plano
                        </Botao>
                    </div>
                </Cartao>

                <ContactosDeAjuda />
            </div>
        </>
    );
}
